import * as RadixToast from '@radix-ui/react-toast';
import { useCopyToClipboard } from 'usehooks-ts';
import { Icon } from '~/components/granular';
import type { IconType } from '~/components/icons';
import { classNames } from '~/utils/styles';
import type { ToastProps } from './toast.types';

const toastIconMap: Record<ToastProps['appearance'] & string, IconType> = {
  success: 'CheckboxCircleIcon',
  error: 'WarningIcon',
  info: 'InformationIcon',
  warning: 'InformationIcon',
};

export function Toast({ open, title, description, copyText, appearance = 'success', onOpenChange }: ToastProps) {
  const [_copiedText, copy] = useCopyToClipboard();

  const handleCopy = (text: string) => () => {
    copy(text);
  };

  const className = classNames(
    "grid grid-cols-[auto_1fr_max-content_max-content] items-start gap-x-2 p-3 shadow-md [grid-template-areas:'icon_title_action_close'_'icon_description_action_close'] data-[state=closed]:animate-hide data-[state=open]:animate-slideIn data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x)",
    { 'border border-green-700 bg-green-100 text-green-700': appearance === 'success' },
    { 'border border-red-700 bg-red-100 text-red-700': appearance === 'error' },
    { 'border border-technology-hydrogenEngine bg-white text-technology-hydrogenEngine': appearance === 'info' },
    { 'border border-yellow-700 bg-yellow-100 text-yellow-700': appearance === 'warning' },
  );

  const titleClassName = classNames('body-200-heavy [grid-area:title]', {
    flex: !description,
  });

  return (
    <RadixToast.Root className={className} onOpenChange={onOpenChange} open={open}>
      <Icon icon={toastIconMap[appearance]} height={20} width={20} className="shrink-0 [grid-area:icon]" />

      {title ? <RadixToast.Title className={titleClassName}>{title}</RadixToast.Title> : null}

      {description ? (
        <RadixToast.Description asChild>
          <p className="body-200-light [grid-area:description]">{description}</p>
        </RadixToast.Description>
      ) : null}

      {copyText ? (
        <RadixToast.Action altText="Copy" asChild className="self-center [grid-area:action]">
          <button aria-label="Copy" onClick={handleCopy(copyText)} type="button">
            <Icon height={16} icon="CopyIcon" width={16} />
          </button>
        </RadixToast.Action>
      ) : null}

      <RadixToast.Close aria-label="Close" className="flex self-center [grid-area:close]">
        <Icon icon="CloseIcon" height={20} width={20} />
      </RadixToast.Close>
    </RadixToast.Root>
  );
}

export function ToastViewport() {
  return (
    <RadixToast.Viewport className="fixed right-0 bottom-0 z-2147483647 m-0 flex w-[450px] max-w-[100vw] list-none flex-col gap-[10px] p-(--viewport-padding) outline-hidden [--viewport-padding:16px]" />
  );
}
